import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Form,
  FormGroup,
  FormLabel,
  FormControl,
  Button,
} from "react-bootstrap";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MainLogo from "../../../../images/main-logo.svg";
import LoginImage from "../../../../images/signup-image.png";
import AuthLeftSection from "../AuthLeftSection/AuthLeftSection.tsx";
import AuthBackground from "../AuthBackground/AuthBackground.tsx";
import apiConfig from "../../config/apiConfig.js";
import { signUpSchema } from '../utils/validationSchema.js';
import { 
  createPhoneChangeHandler, 
  handlePhoneKeyDown, 
  getRawPhoneDigits,
  enhancedSanitizers 
} from '../utils/phoneFormatter.js';
import { locationApi } from '../utils/LocationApi.tsx';
import { API_ENDPOINTS } from '../utils/constants.js';
import SearchableSelect from '../../../common/SearchableSelect/SearchableSelect.tsx';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./SignUp.scss";

function SignUp() {
  const navigate = useNavigate();
  
  // State for dropdowns
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);

  // Fetch states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        setLoadingStates(true);
        const response = await locationApi.getAllStates();
        
        if (response.status === 'Success' && response.states) {
          // console.log('States loaded:', response.states);
          setStates(response.states);
        } else {
          toast.error('Failed to load states. Please refresh the page.');
        }
      } catch (error) {
        // console.error('Error fetching states:', error);
        toast.error('Failed to load states. Please refresh the page.');
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
  }, []);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "+1",
      address1: "",
      address2: "",
      state: "",
      city: "",
      zip: "",
      referralCode: "",
      termsAccepted: false,
    },
    validationSchema: signUpSchema,
    onSubmit: async (values, { setSubmitting, setFieldError, setFieldTouched }) => {
      try {
        // console.log('Form values on submit:', values);
        // console.log('Current states array:', states);
        // console.log('Current cities array:', cities);
        
        // Find the selected state and city names instead of IDs
        // console.log('Looking for state with _id:', values.state);
        // console.log('Looking for city with _id:', values.city);
        
        const selectedState = states.find(state => {
          // console.log('Comparing state._id:', state._id, 'with values.state:', values.state);
          return state._id === values.state;
        });
        const selectedCity = cities.find(city => {
          // console.log('Comparing city._id:', city._id, 'with values.city:', values.city);
          return city._id === values.city;
        });
        
        // console.log('Selected state for submission:', selectedState);
        // console.log('Selected city for submission:', selectedCity);
        
        // console.log('Final state value:', selectedState?.name || values.state);
        // console.log('Final city value:', selectedCity?.name || values.city);

        const requestPayload = {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email.trim(),
          phoneNumber: getRawPhoneDigits(values.phoneNumber),
          address1: values.address1.trim(),
          address2: values.address2.trim(),
          state: selectedState?.name || values.state,
          city: selectedCity?.name || values.city,
          zip: values.zip.trim(),
          referralCode: values.referralCode.trim() || "",
          acceptedTerms: values.termsAccepted,
        };
        
        // console.log('Request payload being sent to API:', requestPayload);

        const response = await axios.post(
          `${apiConfig.apiBaseURL}${API_ENDPOINTS.REGISTER}`,
          requestPayload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (response.data.status === "Success") {
          localStorage.setItem(
            "pendingUser",
            JSON.stringify({
              email: response.data.email,
              userId: response.data.userId,
            })
          );
          
          toast.success(
            response.data.message ||
              "Account created successfully! Please verify your email."
          );
          
          navigate("/otp", {
            state: {
              flow: "signup",
              email: response.data.email
            }
          });
        } else {
          // Handle email already registered case
          if (response?.data?.email === true) {
            setFieldError("email", response.data.message || "Email already registered.");
            setFieldTouched("email", true, false);
          }
          
          toast.error(
            response.data.message ||
              "Account creation failed. Please try again."
          );
        }
      } catch (error) {
        // Handle error response from server
        if (error.response?.data?.email === true) {
          setFieldError("email", error.response.data.message || "Email already registered.");
          setFieldTouched("email", true, false);
        }
        
        toast.error(
          error.response?.data?.message || "Account creation failed. Please try again."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Create phone change handler using the utility
  const handlePhoneChange = createPhoneChangeHandler(formik, "phoneNumber");

  // Handle state change and fetch cities
  const handleStateChange = async (e) => {
    const selectedStateId = e.target.value;
    // console.log('Selected state ID:', selectedStateId);

    const selectedState = states.find(state => state._id === selectedStateId);
    // console.log('Selected state object:', selectedState);

    formik.setFieldValue("state", selectedStateId);
    formik.setFieldValue("city", ""); // Reset city when state changes
    formik.setFieldValue("zip", ""); // Clear zip code when state changes
    setCities([]); // Clear previous cities

    if (selectedStateId) {
      try {
        setLoadingCities(true);
        const response = await locationApi.getCitiesByStateId(selectedStateId);

        if (response.status === 'Success' && response.cities) {
          // console.log('Cities loaded for state:', response.cities);
          setCities(response.cities);
        } else {
          toast.error('Failed to load cities for selected state.');
        }
      } catch (error) {
        // console.error('Error fetching cities:', error);
        toast.error('Failed to load cities for selected state.');
      } finally {
        setLoadingCities(false);
      }
    }
  };

  const handleValidateAndSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      // Just mark fields as touched to show validation errors below fields
      // No need for toast as Formik already shows errors below each field
      formik.setTouched({
        firstName: true,
        lastName: true,
        email: true,
        phoneNumber: true,
        address1: true,
        state: true,
        city: true,
        zip: true,
        termsAccepted: true,
      });
      return;
    }
    formik.handleSubmit();
  };

  // Animation variants for smooth, calm transitions (same as OTP screen)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        staggerChildren: 0.2
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -60, scale: 0.95 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 1,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 60, scale: 0.95 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 1,
        ease: [0.25, 0.46, 0.45, 0.94],
        delay: 0.3
      }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const floatingAnimation = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <>
      {/* Full-screen background animations */}
      <AuthBackground />

      <motion.section
        className="login-screen"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 15px', position: 'relative', zIndex: 1 }}
      >
        <div style={{ maxWidth: '1600px', width: '100%' }}>
        <div className="row" style={{ width: '100%', margin: '0' }}>
        {/* Left Panel - Enhanced Image and Content */}
        <AuthLeftSection
          image={LoginImage}
          imageAlt="SignUp"
          title="Give the Gift of Connection"
          description="In a world where love knows no bounds, Gifts from Heaven exists to keep your presence alive in the hearts of your loved ones—even after you’re gone."
          slideInLeft={slideInLeft}
          fadeInUp={fadeInUp}
          floatingAnimation={floatingAnimation}
        />

        {/* RIGHT FORM SIDE */}
        <div className="col-md-6" style={{ display: 'flex' }}>
          {/* OUTER WRAPPER - Has background and padding */}
          <motion.div
            className="login-screen-right-wrapper"
            variants={slideInRight}
            initial="hidden"
            animate="visible"
            style={{
              height: 'calc(100vh - 20px)',
              width: '100%',
              background: '#F2F3F7',
              borderRadius: '24px'
            }}
          >
            {/* INNER SCROLLABLE CONTAINER */}
            <div
              className="login-screen-right"
              style={{
                height: '100%',
                overflowY: 'auto',
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div className="login-screen-right-in" style={{ justifyContent: 'flex-start', height: 'auto', minHeight: '100%', background: 'transparent' }}>
            <div className="middle-block">
              <motion.figure
                className="main-logo"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  duration: 0.8,
                  ease: "easeOut",
                  delay: 0.8
                }}
                whileHover={{ scale: 1.05 }}
              >
                <img src={MainLogo} alt="Main Logo" />
              </motion.figure>

              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  ease: "easeOut",
                  delay: 1
                }}
                style={{ marginBottom: '1rem', color: '#000', fontSize: '30px', fontWeight: 700 }}
              >
                Create your account
              </motion.h3>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.6 }}
              >
                <Form onSubmit={formik.handleSubmit} className="py-0">
                  {/* First Name */}
                  <FormGroup className="form-group">
                    <FormLabel>First Name *</FormLabel>
                    <FormControl
                      autoFocus
                      type="text"
                      name="firstName"
                      placeholder="Enter First Name"
                      value={formik.values.firstName}
                      onChange={(e) => {
                        const sanitized = enhancedSanitizers.name(e.target.value);
                        formik.setFieldValue("firstName", sanitized);
                      }}
                      className={`form-control p-3`}
                      disabled={formik.isSubmitting}
                    />
                    {formik.touched.firstName && formik.errors.firstName && (
                      <small
                        style={{
                          color: "#dc3545",
                          display: "block",
                          marginTop: "4px",
                          fontSize: "0.875rem",
                        }}
                      >
                        {formik.errors.firstName}
                      </small>
                    )}
                  </FormGroup>

                  {/* Last Name */}
                  <FormGroup className="form-group">
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl
                      type="text"
                      name="lastName"
                      placeholder="Enter Last Name"
                      value={formik.values.lastName}
                      onChange={(e) => {
                        const sanitized = enhancedSanitizers.name(e.target.value);
                        formik.setFieldValue("lastName", sanitized);
                      }}
                      className={`form-control p-3`}
                      disabled={formik.isSubmitting}
                    />
                    {formik.touched.lastName && formik.errors.lastName && (
                      <small
                        style={{
                          color: "#dc3545",
                          display: "block",
                          marginTop: "4px",
                          fontSize: "0.875rem",
                        }}
                      >
                        {formik.errors.lastName}
                      </small>
                    )}
                  </FormGroup>

                  {/* Email Address */}
                  <FormGroup className="form-group">
                    <FormLabel>Email Address *</FormLabel>
                    <FormControl
                      type="email"
                      name="email"
                      placeholder="Email address"
                      value={formik.values.email}
                      onChange={(e) => {
                        const sanitized = enhancedSanitizers.email(e.target.value);
                        formik.setFieldValue("email", sanitized);
                      }}
                      onKeyDown={(e) => e.key === " " && e.preventDefault()}
                      className={`form-control p-3`}
                      disabled={formik.isSubmitting}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <small
                        style={{
                          color: "#dc3545",
                          display: "block",
                          marginTop: "4px",
                          fontSize: "0.875rem",
                        }}
                      >
                        {formik.errors.email}
                      </small>
                    )}
                  </FormGroup>

                  {/* Phone Number */}
                  <FormGroup className="form-group">
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl
                      type="text"
                      name="phoneNumber"
                      placeholder="Enter Phone Number"
                      value={formik.values.phoneNumber}
                      onChange={handlePhoneChange}
                      onKeyDown={handlePhoneKeyDown}
                      className={`form-control p-3`}
                      disabled={formik.isSubmitting}
                    />
                    {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                      <small
                        style={{
                          color: "#dc3545",
                          display: "block",
                          marginTop: "4px",
                          fontSize: "0.875rem",
                        }}
                      >
                        {formik.errors.phoneNumber}
                      </small>
                    )}
                  </FormGroup>

                  {/* Address 1 */}
                  <FormGroup className="form-group">
                    <FormLabel>Address 1 *</FormLabel>
                    <FormControl
                      type="text"
                      name="address1"
                      placeholder="Enter Address"
                      value={formik.values.address1}
                      onChange={(e) => {
                        // console.log('Address1 input value:', e.target.value);
                        // Temporarily bypass sanitizer to test
                        formik.setFieldValue("address1", e.target.value);
                      }}
                      onBlur={formik.handleBlur}
                      className={`form-control p-3`}
                      disabled={formik.isSubmitting}
                    />
                    {formik.touched.address1 && formik.errors.address1 && (
                      <small
                        style={{
                          color: "#dc3545",
                          display: "block",
                          marginTop: "4px",
                          fontSize: "0.875rem",
                        }}
                      >
                        {formik.errors.address1}
                      </small>
                    )}
                  </FormGroup>

                  {/* Address 2 */}
                  <FormGroup className="form-group">
                    <FormLabel>Address 2</FormLabel>
                    <FormControl
                      type="text"
                      name="address2"
                      placeholder="Enter Address Line 2 (Optional)"
                      value={formik.values.address2}
                      onChange={(e) => {
                        // console.log('Address2 input value:', e.target.value);
                        // Temporarily bypass sanitizer to test
                        formik.setFieldValue("address2", e.target.value);
                      }}
                      onBlur={formik.handleBlur}
                      className={`form-control p-3`}
                      disabled={formik.isSubmitting}
                    />
                    {formik.touched.address2 && formik.errors.address2 && (
                      <small
                        style={{
                          color: "#dc3545",
                          display: "block",
                          marginTop: "4px",
                          fontSize: "0.875rem",
                        }}
                      >
                        {formik.errors.address2}
                      </small>
                    )}
                  </FormGroup>

                  {/* State Dropdown */}
                  <FormGroup className="form-group">
                    <FormLabel>State *</FormLabel>
                    <SearchableSelect
                      value={formik.values.state}
                      onChange={(value) => {
                        const event = { target: { name: 'state', value } };
                        handleStateChange(event as any);
                      }}
                      options={states.map(state => ({
                        value: state._id,
                        label: state.name
                      }))}
                      placeholder="Select State"
                      searchPlaceholder="Search states..."
                      loading={loadingStates}
                      loadingText="Loading states..."
                      disabled={formik.isSubmitting || loadingStates}
                      isInvalid={formik.touched.state && !!formik.errors.state && formik.values.state === ''}
                      className="signup-state-select"
                      clearable={true}
                    />
                    {formik.touched.state && formik.errors.state && formik.values.state === '' && (
                      <small
                        style={{
                          color: "#dc3545",
                          display: "block",
                          marginTop: "4px",
                          fontSize: "0.875rem",
                        }}
                      >
                        {formik.errors.state}
                      </small>
                    )}
                  </FormGroup>

                  {/* City Dropdown */}
                  <FormGroup className="form-group">
                    <FormLabel>City *</FormLabel>
                    <SearchableSelect
                      value={formik.values.city}
                      onChange={(value) => {
                        // console.log('Selected city ID:', value);
                        const selectedCity = cities.find(city => city._id === value);
                        // console.log('Selected city object:', selectedCity);
                        formik.setFieldValue('city', value);
                      }}
                      options={cities.map(city => ({
                        value: city._id,
                        label: city.name
                      }))}
                      placeholder={
                        !formik.values.state 
                          ? "Select state first" 
                          : loadingCities 
                          ? "Loading cities..." 
                          : "Select City"
                      }
                      searchPlaceholder="Search cities..."
                      loading={loadingCities}
                      loadingText="Loading cities..."
                      disabled={formik.isSubmitting || loadingCities || !formik.values.state}
                      isInvalid={formik.touched.city && !!formik.errors.city && formik.values.city === ''}
                      className="signup-city-select"
                      clearable={true}
                      noOptionsText={!formik.values.state ? 'Select state first' : 'No cities found'}
                    />
                    {formik.touched.city && formik.errors.city && formik.values.city === '' && (
                      <small
                        style={{
                          color: "#dc3545",
                          display: "block",
                          marginTop: "4px",
                          fontSize: "0.875rem",
                        }}
                      >
                        {formik.errors.city}
                      </small>
                    )}
                  </FormGroup>

                  {/* Zip Code */}
                  <FormGroup className="form-group">
                    <FormLabel>Zip *</FormLabel>
                    <FormControl
                      type="text"
                      name="zip"
                      placeholder="Enter Zip Code"
                      value={formik.values.zip}
                      onChange={(e) => {
                        // Only allow numbers and limit to 5-10 characters
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        formik.setFieldValue("zip", value);
                      }}
                      onBlur={formik.handleBlur}
                      className={`form-control p-3`}
                      disabled={formik.isSubmitting}
                      maxLength={10}
                    />
                    {formik.touched.zip && formik.errors.zip && (
                      <small
                        style={{
                          color: "#dc3545",
                          display: "block",
                          marginTop: "4px",
                          fontSize: "0.875rem",
                        }}
                      >
                        {formik.errors.zip}
                      </small>
                    )}
                  </FormGroup>

                  {/* Referral Code */}
                  <FormGroup className="form-group">
                    <FormLabel>Referral Code</FormLabel>
                    <FormControl
                      type="text"
                      name="referralCode"
                      placeholder="Enter Referral Code"
                      value={formik.values.referralCode}
                      onKeyDown={(e) => e.key === " " && e.preventDefault()}
                      onChange={formik.handleChange}
                      className={`form-control p-3`}
                      disabled={formik.isSubmitting}
                    />
                  </FormGroup>

                  {/* Terms and Conditions */}
                  <FormGroup className="form-group">
                    <Form.Check
                      type="checkbox"
                      id="terms"
                      label={
                        <span>
                          Accept{" "}
                          <b className="terms-text">Terms and Conditions</b>
                        </span>
                      }
                      name="termsAccepted"
                      checked={formik.values.termsAccepted}
                      onChange={formik.handleChange}
                      isInvalid={
                        !!formik.touched.termsAccepted &&
                        !!formik.errors.termsAccepted
                      }
                      disabled={formik.isSubmitting}
                    />
                    {formik.touched.termsAccepted &&
                      formik.errors.termsAccepted && (
                        <small
                          style={{
                            color: "#dc3545",
                            display: "block",
                            marginTop: "4px",
                            fontSize: "0.875rem",
                          }}
                        >
                          {formik.errors.termsAccepted}
                        </small>
                      )}
                  </FormGroup>

                  {/* Submit Button */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      className="main-bttn"
                      type="submit"
                      onClick={handleValidateAndSubmit}
                      disabled={!formik.values.termsAccepted || formik.isSubmitting}
                      style={{
                        width: '100%',
                        padding: '14px 20px',
                        background: formik.isSubmitting || !formik.values.termsAccepted
                          ? 'linear-gradient(135deg, #bbb, #999)' 
                          : '#D7A360',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        boxShadow: '0 8px 20px rgba(215, 163, 96, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <AnimatePresence mode="wait">
                        {formik.isSubmitting ? (
                          <motion.span
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            <motion.span
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              style={{ display: 'inline-block', marginRight: '8px' }}
                            >
                              ⟳
                            </motion.span>
                            Creating Account...
                          </motion.span>
                        ) : (
                          <motion.span
                            key="submit"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            Create Account
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </Button>
                  </motion.div>

                  {/* Sign In Link */}
                  <motion.div
                    className="account"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.8, duration: 0.6 }}
                  >
                    Already have an account?{" "}
                    <Link 
                      to="/" 
                      style={{ 
                        pointerEvents: formik.isSubmitting ? "none" : "auto", 
                        opacity: formik.isSubmitting ? 0.5 : 1 
                      }}
                    >
                      Sign in
                    </Link>
                  </motion.div>
                </Form>
              </motion.div>
            </div>
          </div>
          </div>
          </motion.div>
        </div>
      </div>
      </div>
      </motion.section>
    </>
  );
}

export default SignUp;